export interface SetupStep {
  id: string;
  step: string;
  title: string;
  text: string;
}

/**
 * The six installation steps for GoldWarden on MetaTrader 5.
 *
 * Kept in one place so both the setup UI and the page's `HowTo`
 * structured data stay in sync automatically.
 */
export const SETUP_STEPS: SetupStep[] = [
  {
    id: 'request',
    step: '01',
    title: 'Request the files',
    text: 'Ask in the support chat. We send you the compiled GoldWarden.ex5 file plus a .set preset matched to your account balance.',
  },
  {
    id: 'install',
    step: '02',
    title: 'Install into MetaTrader 5',
    text: 'In MT5 open File → Open Data Folder, go to MQL5 → Experts, and paste the .ex5 file there. Restart MetaTrader 5.',
  },
  {
    id: 'algo',
    step: '03',
    title: 'Enable algorithmic trading',
    text: 'Go to Tools → Options → Expert Advisors and tick "Allow Algo Trading". Then make sure the AutoTrading button on the toolbar is green.',
  },
  {
    id: 'attach',
    step: '04',
    title: 'Attach to one XAUUSD chart',
    text: 'Open one XAUUSD M15 chart, drag GoldWarden onto it and load the .set preset in the Inputs tab. One chart is all that is needed.',
  },
  {
    id: 'risk',
    step: '05',
    title: 'Pick your risk level',
    text: 'The preset sets risk as a fixed share of equity — typically 0.5%–1.5% per trade depending on the preset. Pick the one matching your balance; the EA sizes lots automatically.',
  },
  {
    id: 'live',
    step: '06',
    title: 'Let it trade',
    text: 'A smiley face in the chart corner means it is live. From there GoldWarden manages entries, stops and targets on its own.',
  },
];

export interface HiwFaqItem {
  id: string;
  question: string;
  answer: string;
}

/**
 * Page-specific questions for the How It Works page.
 *
 * These deliberately stay on the *mechanics* of the strategy — entry logic,
 * stops and targets, symbol, trade frequency, news handling and settings.
 * The broader pre-purchase questions (is it free, which broker, minimum
 * deposit, do I need a VPS) already live on the FAQ page, so we do not
 * repeat them here. The page's `FAQPage` structured data is generated from
 * this same array, keeping the markup and the visible copy in sync.
 */
export const HIW_FAQ: HiwFaqItem[] = [
  {
    id: 'entry',
    question: 'How does it decide when to enter?',
    answer:
      'GoldWarden waits for price to break market structure on XAUUSD M15, then enters on the controlled pullback that follows, always in the direction of the prevailing trend. Momentum confirmation, a volatility filter and a news guard all have to line up before an order is sent.',
  },
  {
    id: 'stops',
    question: 'How are the stop-loss and take-profit set?',
    answer:
      'Every trade opens with a hard stop sized to that setup, and the take-profit sits at a measured multiple of the stop distance, so reward-to-risk is fixed before entry. Once a trade has covered its risk the stop moves to break-even and then trails behind price.',
  },
  {
    id: 'symbol',
    question: 'Which symbol and timeframe does it trade?',
    answer:
      'GoldWarden trades one symbol only — XAUUSD (gold) — on the M15 timeframe. You attach it to a single XAUUSD M15 chart, and it does not spread itself across multiple pairs.',
  },
  {
    id: 'frequency',
    question: 'How often does it trade?',
    answer:
      'Only when the filters line up. Activity is concentrated in the London and New York sessions, and quiet days with no entry at all are normal — the EA is built to sit out poor conditions rather than force trades.',
  },
  {
    id: 'news',
    question: 'Does it trade around news?',
    answer:
      'It avoids opening new positions around high-impact US releases and steps aside when gold spreads spike. Existing trades stay protected by their stops rather than being chased into the volatility.',
  },
  {
    id: 'settings',
    question: 'Can I change the strategy settings myself?',
    answer:
      'The preset exposes risk and a handful of core inputs so you can tune how aggressively it trades. The entry and exit logic itself is compiled and fixed, which is what keeps behaviour consistent across every account.',
  },
];